import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Layout } from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import {
  Zap, Plus, Search, MoreHorizontal, Play, Pause,
  Trash2, Copy, Edit, Activity, Loader2, Settings,
  CheckCircle2, AlertCircle, Clock, ArrowRight,
  Mail, MessageSquare, GitBranch, Bot, Layers,
  ChevronLeft, FlaskConical, Eye, RefreshCw
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { cn } from '../../lib/utils';
import { AutomationBuilder } from '../../components/automations/AutomationBuilderV2';
import { AutomationLogsViewer } from '../../components/automations/AutomationLogsViewer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Category configuration matching PM requirements
const AUTOMATION_CATEGORIES = [
  { id: 'all', label: 'All Automations', icon: Layers, color: 'text-slate-400' },
  { id: 'email', label: 'Email', icon: Mail, color: 'text-blue-400' },
  { id: 'sms', label: 'SMS', icon: MessageSquare, color: 'text-green-400' },
  { id: 'status_flow', label: 'Status Flows', icon: GitBranch, color: 'text-purple-400' },
  { id: 'ai', label: 'AI', icon: Bot, color: 'text-amber-400' },
  { id: 'platform', label: 'Platform', icon: Zap, color: 'text-cyan-400' },
];

// Status configuration
const STATUS_CONFIG = {
  active: { 
    label: 'Active', 
    color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    dot: 'bg-emerald-400'
  },
  inactive: { 
    label: 'Inactive', 
    color: 'bg-slate-500/10 text-slate-400 border-slate-500/20',
    dot: 'bg-slate-400'
  },
  draft: { 
    label: 'Draft', 
    color: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    dot: 'bg-amber-400'
  },
};

export default function AutomationsHub() {
  // State
  const [automations, setAutomations] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    recently_triggered: 0,
    failed: 0,
  });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  
  // Modal states
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingAutomation, setEditingAutomation] = useState(null);
  const [showLogs, setShowLogs] = useState(false);
  const [selectedAutomationId, setSelectedAutomationId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Fetch automations
  const fetchAutomations = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (activeCategory && activeCategory !== 'all') {
        params.append('category', activeCategory);
      }
      if (searchQuery) {
        params.append('search', searchQuery);
      }

      const response = await axios.get(`${API}/automations?${params.toString()}`);
      setAutomations(response.data);
    } catch (error) {
      console.error('Error fetching automations:', error);
      toast.error('Failed to load automations');
    } finally {
      setLoading(false);
    }
  }, [activeCategory, searchQuery]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/automations/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      // Use calculated stats from automations as fallback
      if (automations.length > 0) {
        setStats({
          total: automations.length,
          active: automations.filter(a => a.status === 'active').length,
          inactive: automations.filter(a => a.status === 'inactive' || a.status === 'paused').length,
          recently_triggered: automations.filter(a => a.last_executed_at).length,
          failed: automations.reduce((acc, a) => acc + (a.failure_count || 0), 0),
        });
      }
    }
  }, [automations]);

  useEffect(() => {
    fetchAutomations();
  }, [fetchAutomations]);

  useEffect(() => {
    fetchStats();
  }, [automations, fetchStats]);

  // Toggle automation status
  const toggleStatus = async (automation) => {
    const newStatus = automation.status === 'active' ? 'paused' : 'active';
    try {
      await axios.patch(`${API}/automations/${automation.id}`, { status: newStatus });
      toast.success(`Automation ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      fetchAutomations();
    } catch (error) {
      toast.error('Failed to update automation status');
    }
  };

  // Clone automation
  const cloneAutomation = async (automation) => {
    try {
      await axios.post(`${API}/automations/${automation.id}/clone`);
      toast.success('Automation duplicated successfully');
      fetchAutomations();
    } catch (error) {
      toast.error('Failed to duplicate automation');
    }
  };

  // Delete automation
  const handleDelete = async (automationId) => {
    try {
      await axios.delete(`${API}/automations/${automationId}`);
      toast.success('Automation deleted');
      setDeleteConfirm(null);
      fetchAutomations();
    } catch (error) {
      toast.error('Failed to delete automation');
    }
  };

  // Test automation (dry run)
  const testAutomation = async (automation) => {
    try {
      await axios.post(`${API}/automations/${automation.id}/test`);
      toast.success('Test execution started - check logs for results');
    } catch (error) {
      toast.error('Failed to start test execution');
    }
  };

  // Handle builder save
  const handleBuilderSave = async (automationData) => {
    try {
      if (editingAutomation?.id) {
        await axios.patch(`${API}/automations/${editingAutomation.id}`, automationData);
        toast.success('Automation updated successfully');
      } else {
        await axios.post(`${API}/automations`, automationData);
        toast.success('Automation created successfully');
      }
      setShowBuilder(false);
      setEditingAutomation(null);
      fetchAutomations();
    } catch (error) {
      toast.error('Failed to save automation');
    }
  };

  // Filter automations based on search and category
  const filteredAutomations = automations.filter(a => {
    const matchesSearch = !searchQuery || 
      a.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = activeCategory === 'all' || a.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  // If showing builder
  if (showBuilder) {
    return (
      <Layout>
        <AutomationBuilder
          automation={editingAutomation}
          onSave={handleBuilderSave}
          onCancel={() => {
            setShowBuilder(false);
            setEditingAutomation(null);
          }}
        />
      </Layout>
    );
  }

  // If showing logs
  if (showLogs) {
    return (
      <Layout>
        <AutomationLogsViewer
          automationId={selectedAutomationId}
          onBack={() => {
            setShowLogs(false);
            setSelectedAutomationId(null);
          }}
        />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-10 max-w-[1600px] mx-auto space-y-8" data-testid="automations-hub">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3" data-testid="automations-title">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Zap className="h-7 w-7 text-primary" />
              </div>
              Automations Hub
            </h1>
            <p className="text-muted-foreground mt-1">
              Build and manage automated workflows for your business
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => { setShowLogs(true); setSelectedAutomationId(null); }}
              data-testid="view-all-logs-btn"
            >
              <Activity className="h-4 w-4 mr-2" />
              View Logs
            </Button>
            <Button 
              onClick={() => { setEditingAutomation(null); setShowBuilder(true); }}
              className="shadow-[0_0_15px_rgba(59,130,246,0.3)]"
              data-testid="create-automation-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Automation
            </Button>
          </div>
        </div>

        {/* Stats Cards - Bento Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <Card className="bg-card/50 border-white/5 hover:border-primary/30 transition-colors group">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Total</p>
                  <p className="text-3xl font-bold mt-1">{stats.total || automations.length}</p>
                </div>
                <div className="p-3 bg-slate-500/10 rounded-lg group-hover:bg-slate-500/20 transition-colors">
                  <Layers className="h-5 w-5 text-slate-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card/50 border-white/5 hover:border-emerald-500/30 transition-colors group">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Active</p>
                  <p className="text-3xl font-bold mt-1 text-emerald-400">{stats.active || stats.total_active || 0}</p>
                </div>
                <div className="p-3 bg-emerald-500/10 rounded-lg group-hover:bg-emerald-500/20 transition-colors">
                  <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card/50 border-white/5 hover:border-slate-400/30 transition-colors group">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Inactive</p>
                  <p className="text-3xl font-bold mt-1 text-slate-400">{stats.inactive || stats.total_paused || 0}</p>
                </div>
                <div className="p-3 bg-slate-500/10 rounded-lg group-hover:bg-slate-500/20 transition-colors">
                  <Pause className="h-5 w-5 text-slate-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card/50 border-white/5 hover:border-blue-500/30 transition-colors group">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Runs Today</p>
                  <p className="text-3xl font-bold mt-1 text-blue-400">{stats.executions_today || 0}</p>
                </div>
                <div className="p-3 bg-blue-500/10 rounded-lg group-hover:bg-blue-500/20 transition-colors">
                  <Clock className="h-5 w-5 text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card/50 border-white/5 hover:border-red-500/30 transition-colors group">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Errors</p>
                  <p className={cn("text-3xl font-bold mt-1", stats.errors_today > 0 ? "text-red-400" : "text-slate-400")}>
                    {stats.errors_today || stats.failed || 0}
                  </p>
                </div>
                <div className={cn(
                  "p-3 rounded-lg transition-colors",
                  stats.errors_today > 0 ? "bg-red-500/10 group-hover:bg-red-500/20" : "bg-slate-500/10 group-hover:bg-slate-500/20"
                )}>
                  <AlertCircle className={cn("h-5 w-5", stats.errors_today > 0 ? "text-red-400" : "text-slate-400")} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Category Tabs + Search */}
        <div className="space-y-4">
          <Tabs value={activeCategory} onValueChange={setActiveCategory} className="w-full">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <TabsList className="bg-card/50 border border-white/5 p-1 h-auto flex-wrap">
                {AUTOMATION_CATEGORIES.map(cat => {
                  const Icon = cat.icon;
                  const count = cat.id === 'all' 
                    ? automations.length 
                    : automations.filter(a => a.category === cat.id).length;
                  return (
                    <TabsTrigger 
                      key={cat.id} 
                      value={cat.id}
                      className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary px-4 py-2 gap-2"
                      data-testid={`tab-${cat.id}`}
                    >
                      <Icon className={cn("h-4 w-4", cat.color)} />
                      {cat.label}
                      <Badge variant="secondary" className="ml-1 text-xs">
                        {count}
                      </Badge>
                    </TabsTrigger>
                  );
                })}
              </TabsList>

              <div className="flex items-center gap-3">
                <div className="relative flex-1 md:w-[300px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search automations..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 bg-card/50 border-white/10"
                    data-testid="search-automations"
                  />
                </div>
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={() => fetchAutomations()}
                  data-testid="refresh-btn"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Tabs>
        </div>

        {/* Automations List */}
        <Card className="bg-card/50 border-white/5">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : filteredAutomations.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
                <div className="p-4 bg-primary/5 rounded-full mb-4">
                  <Zap className="h-12 w-12 opacity-50" />
                </div>
                <p className="text-lg font-medium">No automations found</p>
                <p className="text-sm mb-4">
                  {searchQuery ? 'Try adjusting your search' : 'Create your first automation to get started'}
                </p>
                {!searchQuery && (
                  <Button 
                    onClick={() => { setEditingAutomation(null); setShowBuilder(true); }}
                    data-testid="empty-create-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Create Automation
                  </Button>
                )}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-white/5 hover:bg-transparent">
                    <TableHead className="w-[300px]">Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Trigger</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Last Run</TableHead>
                    <TableHead>Executions</TableHead>
                    <TableHead className="w-[100px] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAutomations.map(automation => {
                    const statusConfig = STATUS_CONFIG[automation.status] || STATUS_CONFIG.draft;
                    const categoryConfig = AUTOMATION_CATEGORIES.find(c => c.id === automation.category) || AUTOMATION_CATEGORIES[0];
                    const CategoryIcon = categoryConfig.icon;
                    
                    return (
                      <TableRow 
                        key={automation.id} 
                        className="border-white/5 hover:bg-white/5 cursor-pointer group"
                        onClick={() => { setEditingAutomation(automation); setShowBuilder(true); }}
                        data-testid={`automation-row-${automation.id}`}
                      >
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className={cn(
                              "w-2 h-2 rounded-full",
                              statusConfig.dot,
                              automation.status === 'active' && "animate-pulse"
                            )} />
                            <div>
                              <div className="font-medium group-hover:text-primary transition-colors">
                                {automation.name}
                              </div>
                              {automation.description && (
                                <div className="text-xs text-muted-foreground truncate max-w-[250px]">
                                  {automation.description}
                                </div>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={cn("border-white/10", categoryConfig.color)}>
                            <CategoryIcon className="h-3 w-3 mr-1" />
                            {categoryConfig.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {automation.triggers?.slice(0, 2).map((trigger, idx) => (
                              <Badge key={idx} variant="secondary" className="text-xs bg-white/5">
                                {trigger.type?.replace(/_/g, ' ')}
                              </Badge>
                            ))}
                            {automation.triggers?.length > 2 && (
                              <Badge variant="secondary" className="text-xs">
                                +{automation.triggers.length - 2}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={automation.status === 'active'}
                              onCheckedChange={() => toggleStatus(automation)}
                              data-testid={`toggle-${automation.id}`}
                            />
                            <Badge className={cn("border", statusConfig.color)}>
                              {statusConfig.label}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {automation.last_executed_at
                            ? formatDistanceToNow(new Date(automation.last_executed_at), { addSuffix: true })
                            : 'Never'
                          }
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <span className="text-emerald-400">{automation.success_count || 0}</span>
                            <span className="text-muted-foreground mx-1">/</span>
                            <span className="text-red-400">{automation.failure_count || 0}</span>
                          </div>
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              <DropdownMenuItem onClick={() => { setEditingAutomation(automation); setShowBuilder(true); }}>
                                <Edit className="h-4 w-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => testAutomation(automation)}>
                                <FlaskConical className="h-4 w-4 mr-2" />
                                Test Run
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => { setSelectedAutomationId(automation.id); setShowLogs(true); }}>
                                <Activity className="h-4 w-4 mr-2" />
                                View Logs
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => cloneAutomation(automation)}>
                                <Copy className="h-4 w-4 mr-2" />
                                Duplicate
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={() => setDeleteConfirm(automation)}
                                className="text-red-400 focus:text-red-400"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Delete Confirmation Dialog */}
        <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-red-400">Delete Automation</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete &quot;{deleteConfirm?.name}&quot;? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={() => handleDelete(deleteConfirm?.id)}
                data-testid="confirm-delete-btn"
              >
                Delete Automation
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
